import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, 
  Loader2, 
  ClipboardCheck, 
  Eye, 
  Car, 
  AlertCircle,
  Clock,
  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { jobCardService } from '../../../../services/apiService';

const STATUS_LABELS = {
  Created: 'Garage reviewing',
  Intake: 'Intake scheduled',
  PendingCustomerApproval: 'Approval Required',
  CustomerApproved: 'Approved',
  CustomerRejected: 'Rejected',
  InProgress: 'In Progress',
  ReadyForQC: 'QC Stage',
  QCRejected: 'QC — garage updating',
  QCApproved: 'QC Approved',
  Billed: 'Invoice Ready',
  Paid: 'Paid',
  Delivered: 'Delivered',
  Closed: 'Closed',
  Cancelled: 'Cancelled',
};

const STATUS_COLORS = {
  Created: 'bg-slate-100 text-slate-800',
  Intake: 'bg-indigo-100 text-indigo-800',
  PendingCustomerApproval: 'bg-amber-100 text-amber-800 border-amber-200 shadow-sm shadow-amber-100',
  CustomerApproved: 'bg-emerald-100 text-emerald-800',
  CustomerRejected: 'bg-red-100 text-red-800',
  InProgress: 'bg-blue-100 text-blue-800',
  ReadyForQC: 'bg-teal-100 text-teal-800',
  QCRejected: 'bg-orange-100 text-orange-900',
  QCApproved: 'bg-indigo-100 text-indigo-800',
  Billed: 'bg-purple-100 text-purple-800',
  Paid: 'bg-green-100 text-green-800',
  Delivered: 'bg-green-100 text-green-800',
  Closed: 'bg-gray-200 text-gray-800',
  Cancelled: 'bg-gray-100 text-gray-600',
};

const Jobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const data = await jobCardService.getMy();
      const list = Array.isArray(data) ? data : data?.jobCards ?? data?.jobs ?? [];
      setJobs(Array.isArray(list) ? list : []);
    } catch (e) {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const pendingCount = jobs.filter((j) => j.status === 'PendingCustomerApproval').length;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Loader2 className="animate-spin text-4xl text-primary-500 mb-4" />
        <p className="text-slate-500 font-medium animate-pulse">Fetching your service history...</p>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-6"
    >
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <motion.h2 variants={itemVariants} className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Service Jobs</motion.h2>
          <motion.p variants={itemVariants} className="text-slate-500 mt-1">Track and manage your vehicle maintenance history.</motion.p>
        </div>
        <motion.div variants={itemVariants}>
          <button 
            onClick={() => navigate('/service/booking')}
            className="px-5 py-2.5 bg-red-600 text-white rounded-xl font-semibold shadow-sm hover:bg-red-700 transition-all flex items-center gap-2 active:scale-95"
          >
            New Booking <ArrowRight size={18} />
          </button>
        </motion.div>
      </header>

      <AnimatePresence>
        {pendingCount > 0 && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                <AlertCircle size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold text-amber-900">Action Required</h4>
                <p className="text-sm text-amber-700">
                  You have {pendingCount} estimate{pendingCount > 1 ? 's' : ''} awaiting your approval.
                </p>
              </div>
              <button 
                onClick={() => {
                  const firstPending = jobs.find(j => j.status === 'PendingCustomerApproval');
                  if (firstPending) navigate(`/service/job/${firstPending._id || firstPending.id}`);
                }}
                className="shrink-0 px-4 py-2 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700 transition-all"
              >
                REVIEW NOW
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {jobs.length === 0 ? (
        <motion.div 
          variants={itemVariants}
          className="bg-white/50 backdrop-blur-sm rounded-3xl border border-dashed border-slate-300 p-20 text-center"
        >
          <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-300 mx-auto mb-6">
            <Settings size={40} />
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No service jobs yet</h3>
          <p className="text-slate-500 mb-8 max-w-sm mx-auto">Keep your vehicle in top condition by booking your first professional maintenance today.</p>
          <button
            onClick={() => navigate('/service/booking')}
            className="px-8 py-4 bg-slate-900 text-white font-bold rounded-2xl hover:bg-slate-800 transition-all shadow-xl active:scale-95"
          >
            Book Your First Service
          </button>
        </motion.div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            (() => {
              const allocStatus = job?.allocation?.status || '';
              const showReallocated = allocStatus === 'reallocate';
              const qcApprovedBySuperadmin =
                job?.qc?.approvedByRole === 'superadmin' ||
                (!job?.qc?.approvedByRole &&
                  (job?.timeline || []).some((t) => t?.status === 'QCApproved' && t?.byRole === 'superadmin'));
              const effectiveStatus = (() => {
                if (job.status === 'QCApproved') return qcApprovedBySuperadmin ? 'Billed' : 'InProgress';
                if (job.status === 'ReadyForQC' || job.status === 'QCRejected') return 'InProgress';
                return job.status;
              })();
              const computedStatusLabel = (() => {
                if (showReallocated) return 'Reallocated by garage';
                if (job.status === 'Created') {
                  if (allocStatus === 'confirmed') return 'Accepted (intake pending)';
                  return 'Garage reviewing';
                }
                return STATUS_LABELS[effectiveStatus] || effectiveStatus;
              })();
              const badgeStyle = showReallocated
                  ? 'bg-amber-100 text-amber-900 border-amber-200'
                  : (STATUS_COLORS[effectiveStatus] || 'bg-gray-100 text-gray-800 border-gray-200');
              const isPending = job.status === 'PendingCustomerApproval';
              const jobId = job._id || job.id;

              return (
                <motion.div
                  key={jobId}
                  variants={itemVariants}
                  className={`group bg-white rounded-xl border transition-all hover:shadow-md ${
                    isPending
                      ? 'border-amber-300 ring-2 ring-amber-500/10'
                      : 'border-slate-100 hover:border-slate-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                    <div
                      className="flex-1 p-4 cursor-pointer min-w-0"
                      onClick={() => navigate(`/service/job/${jobId}`)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          isPending ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-500'
                        }`}>
                          <Settings size={18} className={job.status === 'InProgress' ? 'animate-spin-slow' : ''} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-bold text-slate-900 text-sm sm:text-base">
                              JOB #{String(job.jobCardId || jobId || '').slice(-8).toUpperCase()}
                            </h4>
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${badgeStyle}`}>
                              {computedStatusLabel}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Clock size={11} />
                              {new Date(job.createdAt || Date.now()).toLocaleDateString()}
                            </span>
                            {job.vehicleInfo && (
                              <span className="flex items-center gap-1">
                                <Car size={11} />
                                {job.vehicleInfo.make} {job.vehicleInfo.model}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 sm:p-0 sm:w-36 shrink-0 flex items-center justify-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/service/job/${jobId}`);
                        }}
                        className={`w-full sm:min-h-[72px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg sm:rounded-none text-xs font-bold transition-all ${
                          isPending
                            ? 'bg-amber-600 text-white hover:bg-amber-700'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                      >
                        {isPending ? 'REVIEW NOW' : 'VIEW DETAILS'}
                        <Eye size={14} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })()
          ))}
        </div>
      )}
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </motion.div>
  );
};

export default Jobs;
